import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, CardContent, Chip, Stack, Divider,
  TextField, MenuItem, InputAdornment, useTheme, alpha,
} from '@mui/material';
import {
  NewReleases as NewReleasesIcon,
  BugReport as BugReportIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  TrendingUp as TrendingUpIcon,
  Build as BuildIcon,
  Lock as LockIcon,
  ReportProblem as ReportProblemIcon,
  CalendarMonth as CalendarIcon,
  Construction as ConstructionIcon,
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { releases, ReleaseEntryType } from '../data/releases';
import { bugs, Bug, BugCategory, BugPriority, BugStatus } from '../data/bugs';
import { APP_VERSION, VERSION_LABEL } from '../data/version';
import Roadmap from './Roadmap';

// ─── Hilfsfunktionen für Farben/Icons ─────────────────────────────────────────
const entryTypeMeta: Record<ReleaseEntryType, { label: string; color: 'success' | 'info' | 'warning' | 'error' | 'secondary'; icon: React.ReactElement }> = {
  feature:     { label: 'Feature',      color: 'success',   icon: <AutoAwesomeIcon sx={{ fontSize: 14 }} /> },
  improvement: { label: 'Verbesserung', color: 'info',      icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  bugfix:      { label: 'Bugfix',       color: 'warning',   icon: <BuildIcon sx={{ fontSize: 14 }} /> },
  security:    { label: 'Sicherheit',   color: 'error',     icon: <LockIcon sx={{ fontSize: 14 }} /> },
  breaking:    { label: 'Breaking',     color: 'secondary', icon: <ReportProblemIcon sx={{ fontSize: 14 }} /> },
};

const priorityColor = (p: BugPriority): 'error' | 'warning' | 'info' | 'default' => {
  switch (p) {
    case 'kritisch': return 'error';
    case 'hoch':     return 'warning';
    case 'mittel':   return 'info';
    case 'niedrig':  return 'default';
  }
};

const statusColor = (s: BugStatus): 'default' | 'info' | 'success' | 'warning' => {
  switch (s) {
    case 'offen':     return 'default';
    case 'in Arbeit': return 'info';
    case 'erledigt':  return 'success';
    case 'verworfen': return 'warning';
  }
};

const categoryIcon = (c: BugCategory): React.ReactElement => {
  switch (c) {
    case 'Bug':          return <BuildIcon sx={{ fontSize: 14 }} />;
    case 'Verbesserung': return <TrendingUpIcon sx={{ fontSize: 14 }} />;
    case 'Feature':      return <AutoAwesomeIcon sx={{ fontSize: 14 }} />;
    case 'Sicherheit':   return <LockIcon sx={{ fontSize: 14 }} />;
  }
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function ReleaseNotes() {
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  // Filter-State für den Roadmap-Tab
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<BugStatus | 'alle'>('alle');
  const [filterCategory, setFilterCategory] = useState<BugCategory | 'alle'>('alle');
  const [filterPriority, setFilterPriority] = useState<BugPriority | 'alle'>('alle');

  const filteredBugs = useMemo<Bug[]>(() => {
    return bugs.filter((b) => {
      if (filterStatus !== 'alle' && b.status !== filterStatus) return false;
      if (filterCategory !== 'alle' && b.category !== filterCategory) return false;
      if (filterPriority !== 'alle' && b.priority !== filterPriority) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${b.id} ${b.title} ${b.description ?? ''} ${b.area ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [search, filterStatus, filterCategory, filterPriority]);

  // Statistiken für Header-Chips
  const stats = useMemo(() => {
    const offen     = bugs.filter((b) => b.status === 'offen').length;
    const inArbeit  = bugs.filter((b) => b.status === 'in Arbeit').length;
    const erledigt  = bugs.filter((b) => b.status === 'erledigt').length;
    return { offen, inArbeit, erledigt };
  }, []);

  return (
    <Box>
      {/* ─── Header ────────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Release Notes
          </Typography>
          <Chip
            label={VERSION_LABEL ? `${VERSION_LABEL} – v${APP_VERSION}` : `v${APP_VERSION}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 600, letterSpacing: 0.5 }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Versionshistorie, geplante Verbesserungen und bekannte Fehler.
        </Typography>
      </Box>

      {/* ─── Tabs ──────────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab
            icon={<NewReleasesIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={`Versionen (${releases.length})`}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            icon={<BugReportIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={`Bugs & Änderungen (${stats.offen + stats.inArbeit})`}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            icon={<LightbulbIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Roadmap"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
      </Box>

      {/* ─── Tab 1: Versionen ──────────────────────────────────────────────── */}
      {tab === 0 && (
        <Stack spacing={2.5}>
          {releases.map((rel, idx) => {
            const isCurrent = rel.version === APP_VERSION && !rel.unreleased;
            const isUnreleased = !!rel.unreleased;
            const borderColor = isUnreleased
              ? 'warning.main'
              : isCurrent
                ? 'primary.main'
                : 'divider';
            return (
              <Card
                key={rel.version}
                variant="outlined"
                sx={{
                  position: 'relative',
                  borderColor,
                  borderWidth: isCurrent || isUnreleased ? 2 : 1,
                  bgcolor: 'background.paper',
                  borderStyle: isUnreleased ? 'dashed' : 'solid',
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  {/* Versions-Header */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 2 }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: isUnreleased ? 'warning.main' : isCurrent ? 'primary.main' : 'text.primary',
                      }}
                    >
                      v{rel.version}
                    </Typography>
                    {isUnreleased && (
                      <Chip
                        icon={<ConstructionIcon sx={{ fontSize: 14 }} />}
                        label="In Entwicklung"
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {isCurrent && (
                      <Chip label="Aktuell" color="primary" size="small" sx={{ fontWeight: 600 }} />
                    )}
                    {!isUnreleased && !isCurrent && idx === 0 && (
                      <Chip label="Neueste" color="success" size="small" />
                    )}
                    <Box sx={{ flex: 1 }} />
                    {!isUnreleased && (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
                        <CalendarIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{formatDate(rel.date)}</Typography>
                      </Stack>
                    )}
                  </Stack>

                  {rel.title && (
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: rel.highlights ? 1 : 1.5 }}>
                      {rel.title}
                    </Typography>
                  )}

                  {rel.highlights && rel.highlights.length > 0 && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                        borderLeft: 3,
                        borderColor: 'primary.main',
                      }}
                    >
                      <Stack spacing={0.5}>
                        {rel.highlights.map((h, i) => (
                          <Typography key={i} variant="body2" sx={{ fontWeight: 500 }}>
                            • {h}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {isUnreleased && rel.entries.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
                      Noch keine Einträge – wird bei jedem erledigten Punkt ergänzt.
                    </Typography>
                  )}

                  {rel.entries.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack spacing={1}>
                        {rel.entries.map((e, i) => {
                          const meta = entryTypeMeta[e.type];
                          return (
                            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                              <Chip
                                icon={meta.icon}
                                label={meta.label}
                                size="small"
                                color={meta.color}
                                variant="outlined"
                                sx={{ minWidth: 110, justifyContent: 'flex-start', fontWeight: 600 }}
                              />
                              <Typography variant="body2" sx={{ flex: 1, pt: 0.4 }}>
                                {e.text}
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* ─── Tab 2: Bugs / Roadmap ─────────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          {/* Statistik-Chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip label={`${stats.offen} offen`} variant="outlined" />
            <Chip label={`${stats.inArbeit} in Arbeit`} color="info" variant="outlined" />
            <Chip label={`${stats.erledigt} erledigt`} color="success" variant="outlined" />
          </Stack>

          {/* Filter */}
          <Card variant="outlined" sx={{ mb: 2, bgcolor: 'background.paper' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                sx={{ alignItems: { md: 'center' } }}
              >
                <TextField
                  size="small"
                  placeholder="Suche…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  select size="small" label="Status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="alle">Alle</MenuItem>
                  <MenuItem value="offen">Offen</MenuItem>
                  <MenuItem value="in Arbeit">In Arbeit</MenuItem>
                  <MenuItem value="erledigt">Erledigt</MenuItem>
                  <MenuItem value="verworfen">Verworfen</MenuItem>
                </TextField>
                <TextField
                  select size="small" label="Kategorie"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value as any)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="alle">Alle</MenuItem>
                  <MenuItem value="Bug">Bug</MenuItem>
                  <MenuItem value="Verbesserung">Verbesserung</MenuItem>
                  <MenuItem value="Feature">Feature</MenuItem>
                  <MenuItem value="Sicherheit">Sicherheit</MenuItem>
                </TextField>
                <TextField
                  select size="small" label="Priorität"
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value as any)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="alle">Alle</MenuItem>
                  <MenuItem value="kritisch">Kritisch</MenuItem>
                  <MenuItem value="hoch">Hoch</MenuItem>
                  <MenuItem value="mittel">Mittel</MenuItem>
                  <MenuItem value="niedrig">Niedrig</MenuItem>
                </TextField>
              </Stack>
            </CardContent>
          </Card>

          {/* Bug-Liste */}
          {filteredBugs.length === 0 ? (
            <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Keine Einträge gefunden.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {filteredBugs.map((b) => (
                <Card
                  key={b.id}
                  variant="outlined"
                  sx={{
                    bgcolor: 'background.paper',
                    opacity: b.status === 'erledigt' || b.status === 'verworfen' ? 0.7 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      {/* ID-Badge */}
                      <Box
                        sx={{
                          minWidth: 72,
                          textAlign: 'center',
                          py: 0.5, px: 1,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.1 : 0.05),
                          fontFamily: 'monospace',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                        }}
                      >
                        {b.id}
                      </Box>

                      {/* Inhalt */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            textDecoration: b.status === 'erledigt' ? 'line-through' : 'none',
                          }}
                        >
                          {b.title}
                        </Typography>
                        {b.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {b.description}
                          </Typography>
                        )}

                        {/* Chips */}
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                          <Chip
                            icon={categoryIcon(b.category)}
                            label={b.category}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={b.priority}
                            size="small"
                            color={priorityColor(b.priority)}
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                          <Chip
                            label={b.status}
                            size="small"
                            color={statusColor(b.status)}
                            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                          />
                          {b.area && (
                            <Chip
                              label={b.area}
                              size="small"
                              variant="outlined"
                              sx={{ color: 'text.secondary' }}
                            />
                          )}
                          {b.status === 'erledigt' && b.resolvedInVersion ? (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              label={`behoben in v${b.resolvedInVersion}`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          ) : (
                            b.targetVersion && (
                              <Chip
                                label={`Ziel: v${b.targetVersion}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            )
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* ─── Tab 3: Roadmap ────────────────────────────────────────────────── */}
      {tab === 2 && <Roadmap />}
    </Box>
  );
}
